/**
 * Cadastro de um trabalhador no terreno, em três passos.
 *
 * Duas escolhas deliberadas:
 *
 * 1. O TELEFONE É VERIFICADO LOGO NO PASSO 1, antes de o agente escrever mais
 *    alguma coisa. Descobrir no fim que a pessoa já está registada, depois de
 *    dez campos e duas fotografias, é a melhor forma de o agente desistir da
 *    aplicação.
 *
 * 2. AS FOTOGRAFIAS SÃO COMPRIMIDAS NO TELEFONE antes de subir. No Zango a
 *    rede é o recurso escasso: uma foto de 4 MB da câmara passa a ~300 KB sem
 *    perder legibilidade do BI.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Camera, Check, Loader2, AlertTriangle, X, RefreshCw,
} from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { guardarRascunho, lerRascunho, apagarRascunho, porNaFila } from '../../utils/offline';

/** Reduz a fotografia antes de a enviar. Devolve um Blob JPEG. */
function comprimir(ficheiro, ladoMaximo = 1600, qualidade = 0.75) {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onerror = () => reject(new Error('Não foi possível ler a fotografia.'));
    leitor.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Fotografia inválida.'));
      img.onload = () => {
        const escala = Math.min(1, ladoMaximo / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.round(img.width * escala);
        c.height = Math.round(img.height * escala);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        c.toBlob((b) => (b ? resolve(b) : reject(new Error('Falha a comprimir.'))), 'image/jpeg', qualidade);
      };
      img.src = leitor.result;
    };
    leitor.readAsDataURL(ficheiro);
  });
}

const Campo = ({ rotulo, id, obrigatorio, ajuda, children }) => (
  <div className="mb-4">
    <label htmlFor={id} className="block text-sm font-medium text-slate-300 mb-1.5">
      {rotulo}{obrigatorio && <span className="text-red-400"> *</span>}
    </label>
    {children}
    {ajuda && <p className="text-xs text-slate-500 mt-1">{ajuda}</p>}
  </div>
);

const entrada = 'w-full px-3 py-3 rounded-xl bg-slate-900 border border-slate-700 text-slate-100 '
  + 'text-base outline-none focus:border-blue-500';

/** Botão de captura: abre directamente a câmara traseira no telemóvel. */
const Captura = ({ nome, rotulo, ficheiro, onEscolher, obrigatorio }) => {
  const ref = useRef(null);
  const [previa, setPrevia] = useState(null);

  useEffect(() => {
    if (!ficheiro) { setPrevia(null); return undefined; }
    const url = URL.createObjectURL(ficheiro);
    setPrevia(url);
    return () => URL.revokeObjectURL(url);
  }, [ficheiro]);

  return (
    <div className="mb-3">
      <input
        ref={ref}
        id={`cap-${nome}`}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={(e) => e.target.files?.[0] && onEscolher(e.target.files[0])}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left
          ${ficheiro ? 'border-green-500/40 bg-green-500/10' : 'border-slate-700 bg-slate-900'}`}
      >
        {previa
          ? <img src={previa} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
          : <span className="grid place-items-center w-14 h-14 rounded-lg bg-slate-800 shrink-0">
              <Camera className="w-6 h-6 text-slate-500" />
            </span>}
        <span className="flex-1 min-w-0">
          <span className="block font-medium">
            {rotulo}{obrigatorio && <span className="text-red-400"> *</span>}
          </span>
          <span className="block text-xs text-slate-500">
            {ficheiro ? 'Tocar para repetir' : 'Tocar para fotografar'}
          </span>
        </span>
        {ficheiro
          ? <Check className="w-5 h-5 text-green-400 shrink-0" />
          : <Camera className="w-5 h-5 text-slate-600 shrink-0" />}
      </button>
    </div>
  );
};

const FieldNewWorker = () => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [passo, setPasso] = useState(1);
  const [zonas, setZonas] = useState([]);
  const [dados, setDados] = useState({
    full_name: '', phone_number: '', date_of_birth: '', zone_id: '',
    profession: '', specialties: '', years_experience: '',
    nif: '', cobra_iva: '',
  });
  const [bi, setBi] = useState('');
  const [fotos, setFotos] = useState({ profile_photo: null, bi_photo: null, bi_verso: null });
  const [duplicado, setDuplicado] = useState(null);
  const [aVerificar, setAVerificar] = useState(false);
  const [aEnviar, setAEnviar] = useState(false);
  const [criado, setCriado] = useState(null);
  const [naFila, setNaFila] = useState(false);
  const [rascunhoLido, setRascunhoLido] = useState(false);

  // Recupera o formulário a meio. O agente pode ter fechado a app, ficado
  // sem bateria, ou atendido uma chamada — nada disso deve custar o trabalho.
  useEffect(() => {
    lerRascunho().then((r) => {
      if (r?.dados?.full_name || r?.dados?.phone_number) {
        setDados(r.dados);
        setBi(r.bi || '');
        setFotos(r.fotos || { profile_photo: null, bi_photo: null, bi_verso: null });
      }
      setRascunhoLido(true);
    }).catch(() => setRascunhoLido(true));
  }, []);

  // Grava enquanto se escreve, mas só depois de ter lido o que lá estava —
  // senão o estado vazio inicial apagava o rascunho recuperado.
  useEffect(() => {
    if (!rascunhoLido || criado) return;
    const t = setTimeout(() => { guardarRascunho({ dados, bi, fotos }); }, 600);
    return () => clearTimeout(t);
  }, [dados, bi, fotos, rascunhoLido, criado]);

  useEffect(() => {
    api.get('/operations/zones').then((r) => {
      const z = r.data || [];
      setZonas(z);
      if (z.length === 1) setDados((d) => ({ ...d, zone_id: String(z[0].zone_id) }));
    }).catch(() => setZonas([]));
  }, []);

  const alterar = (campo) => (e) => {
    setDados((d) => ({ ...d, [campo]: e.target.value }));
    if (campo === 'phone_number') setDuplicado(null);
  };

  /** Verifica o telefone assim que estiver completo — antes do resto. */
  const verificarTelefone = async () => {
    const so = dados.phone_number.replace(/\D/g, '');
    if (so.length < 9) return;
    setAVerificar(true);
    try {
      const { data } = await api.post('/operations/workers/check', { phone_number: dados.phone_number });
      setDuplicado(data?.duplicado ? data : null);
    } catch { /* sem rede: a validação do servidor apanha na submissão */ }
    finally { setAVerificar(false); }
  };

  const podeAvancar = dados.full_name.trim() && dados.phone_number.replace(/\D/g, '').length >= 9
    && dados.zone_id && !duplicado;

  /** Monta o cadastro completo, com as fotografias já comprimidas. */
  const montarEnvio = async () => {
    const fotosComprimidas = {};
    for (const [campo, f] of Object.entries(fotos)) {
      if (f) fotosComprimidas[campo] = await comprimir(f);
    }
    return {
      dados: {
        ...dados,
        zone_id: Number(dados.zone_id),
        years_experience: dados.years_experience ? Number(dados.years_experience) : null,
        specialties: dados.specialties.split(',').map((x) => x.trim()).filter(Boolean),
        bi_number: bi.trim() || null,
      },
      bi_number: bi.trim() || null,
      fotos: fotosComprimidas,
      nome: dados.full_name.trim(),
    };
  };

  const submeter = async () => {
    setAEnviar(true);
    try {
      const envio = await montarEnvio();

      // Sem rede: para a fila. Para o agente o trabalho está feito — a
      // aplicação envia sozinha assim que houver ligação.
      if (!navigator.onLine) {
        await porNaFila(envio);
        await apagarRascunho();
        setNaFila(true);
        setCriado({ full_name: envio.nome, worker_code: null });
        return;
      }

      const { data: novo } = await api.post('/operations/workers', envio.dados);

      if (envio.bi_number && envio.fotos.profile_photo && envio.fotos.bi_photo) {
        const fd = new FormData();
        fd.append('bi_number', envio.bi_number);
        Object.entries(envio.fotos).forEach(([campo, blob]) => {
          if (blob) fd.append(campo, blob, `${campo}.jpg`);
        });
        try {
          await api.post(`/verification/worker/${novo.user_id}`, fd,
            { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (e) {
          // O trabalhador já existe; só as fotografias falharam. Guarda-se o
          // resto na fila com o id, para a retentativa não criar um duplicado.
          await porNaFila({ ...envio, worker_id: novo.user_id, worker_code: novo.worker_code });
          await apagarRascunho();
          setNaFila(true);
          setCriado(novo);
          return;
        }
      }

      await apagarRascunho();
      success(`${novo.worker_code} registado.`);
      setCriado(novo);
    } catch (e) {
      const status = e.response?.status;
      // Falha de rede a meio do envio: guarda em vez de perder o trabalho.
      if (!status) {
        try {
          await porNaFila(await montarEnvio());
          await apagarRascunho();
          setNaFila(true);
          setCriado({ full_name: dados.full_name.trim(), worker_code: null });
          return;
        } catch { /* cai para a mensagem de erro abaixo */ }
      }
      const msg = e.response?.data?.message || 'Não foi possível registar. Tente de novo.';
      error(msg);
      if (status === 409) { setDuplicado(e.response.data); setPasso(1); }
    } finally {
      setAEnviar(false);
    }
  };

  // ── Concluído ──
  if (criado) {
    return (
      <div className="text-center py-10 space-y-5">
        <span className={`grid place-items-center w-16 h-16 rounded-full mx-auto
          ${naFila ? 'bg-amber-500/15 text-amber-400' : 'bg-green-500/15 text-green-400'}`}>
          {naFila ? <RefreshCw className="w-8 h-8" /> : <Check className="w-8 h-8" />}
        </span>
        <div>
          <h2 className="text-xl font-bold">{criado.full_name}</h2>
          {criado.worker_code && <p className="text-blue-400 font-mono mt-1">{criado.worker_code}</p>}
          {/* Sem rede o cadastro fica guardado no telefone. Dizer "enviado"
              seria mentira, e o agente não saberia que tem de voltar a ter
              rede antes de acabar o dia. */}
          <p className="text-sm text-slate-500 mt-2">
            {naFila
              ? 'Guardado no telefone. Envia-se sozinho assim que houver rede — não feche a aplicação antes disso.'
              : 'Enviado para verificação. O Gestor de operações vai analisar.'}
          </p>
        </div>
        <div className="flex flex-col gap-2 pt-2">
          <button
            onClick={() => {
              setCriado(null); setNaFila(false); setPasso(1); setBi('');
              setFotos({ profile_photo: null, bi_photo: null, bi_verso: null });
              setDados((d) => ({
                full_name: '', phone_number: '', date_of_birth: '', zone_id: d.zone_id,
                profession: '', specialties: '', years_experience: '',
              }));
            }}
            className="py-3.5 rounded-xl bg-blue-600 font-semibold"
          >
            Registar outro
          </button>
          <button onClick={() => navigate('/admin')} className="py-3.5 rounded-xl bg-slate-800 font-semibold">
            Voltar ao início
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => (passo === 1 ? navigate('/admin') : setPasso(passo - 1))}
          aria-label="Voltar"
          className="grid place-items-center w-11 h-11 rounded-xl bg-slate-900 border border-slate-800"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="font-bold">Novo worker</h1>
          <p className="text-xs text-slate-500">Passo {passo} de 3</p>
        </div>
      </div>

      <div className="flex gap-1 mb-5">
        {[1, 2, 3].map((n) => (
          <span key={n} className={`h-1 flex-1 rounded-full ${n <= passo ? 'bg-blue-500' : 'bg-slate-800'}`} />
        ))}
      </div>

      {/* ── Passo 1: dados básicos ── */}
      {passo === 1 && (
        <div>
          <Campo rotulo="Nome completo" id="nome" obrigatorio>
            <input id="nome" className={entrada} value={dados.full_name}
              onChange={alterar('full_name')} autoComplete="off" />
          </Campo>

          <Campo rotulo="Telefone" id="tel" obrigatorio ajuda="Número angolano: 9XX XXX XXX">
            <div className="relative">
              <input id="tel" className={entrada} type="tel" inputMode="tel"
                value={dados.phone_number} onChange={alterar('phone_number')}
                onBlur={verificarTelefone} autoComplete="off" />
              {aVerificar && (
                <Loader2 className="w-4 h-4 animate-spin text-slate-500 absolute right-3 top-1/2 -translate-y-1/2" />
              )}
            </div>
          </Campo>

          {duplicado && (
            <div className="flex items-start gap-2 p-3 mb-4 rounded-xl bg-red-500/10 border border-red-500/30">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-red-300">Já está registado</div>
                <p className="text-red-200/80 text-xs mt-0.5">
                  {duplicado.existente?.full_name}
                  {duplicado.existente?.worker_code ? ` — ${duplicado.existente.worker_code}` : ''}.
                  Confirme com a pessoa antes de continuar.
                </p>
              </div>
            </div>
          )}

          <Campo rotulo="Data de nascimento" id="nasc">
            <input id="nasc" className={entrada} type="date"
              value={dados.date_of_birth} onChange={alterar('date_of_birth')}
              onInput={alterar('date_of_birth')} />
          </Campo>

          <Campo rotulo="Zona" id="zona" obrigatorio>
            <select id="zona" className={entrada} value={dados.zone_id} onChange={alterar('zone_id')}>
              <option value="">Escolher…</option>
              {zonas.map((z) => <option key={z.zone_id} value={z.zone_id}>{z.nome}</option>)}
            </select>
          </Campo>

          <Campo rotulo="Profissão" id="prof">
            <input id="prof" className={entrada} value={dados.profession}
              onChange={alterar('profession')} placeholder="Electricista" autoComplete="off" />
          </Campo>

          <Campo rotulo="Especialidades" id="esp" ajuda="Separe por vírgulas">
            <input id="esp" className={entrada} value={dados.specialties}
              onChange={alterar('specialties')} placeholder="Quadros, instalações" autoComplete="off" />
          </Campo>

          <Campo rotulo="Anos de experiência" id="anos">
            <input id="anos" className={entrada} type="number" inputMode="numeric" min="0" max="60"
              value={dados.years_experience} onChange={alterar('years_experience')} />
          </Campo>

          <Campo rotulo="NIF" id="nif" ajuda="Se ainda não tiver, deixe em branco">
            <input id="nif" className={entrada} value={dados.nif}
              onChange={alterar('nif')} placeholder="Opcional" autoComplete="off" />
          </Campo>

          {/*
            Ninguém na rua responde a "qual é o seu regime de IVA?". Mas toda a
            gente sabe responder se passa factura com IVA — e é isso que decide
            se a Dozy acrescenta 14% ao trabalho dele.

            Quem factura menos de 25 milhões por ano está no Regime de Exclusão
            (Lei 14/23, art. 60.º) e não liquida IVA. É a esmagadora maioria.
            "Não sei" fica por apurar em vez de virar um palpite com ar de facto.
          */}
          <Campo rotulo="Cobra IVA nos seus serviços?" id="iva"
                 ajuda="Quem factura menos de 25 milhões por ano normalmente não cobra.">
            <select id="iva" className={entrada} value={dados.cobra_iva}
                    onChange={alterar('cobra_iva')}>
              <option value="">Escolher…</option>
              <option value="nao">Não cobra IVA</option>
              <option value="sim">Sim, cobra IVA nas facturas</option>
              <option value="nao_sei">Não sei / a confirmar</option>
            </select>
          </Campo>

          <button
            onClick={() => setPasso(2)}
            disabled={!podeAvancar}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-blue-600
                       disabled:opacity-40 font-semibold mt-2"
          >
            Continuar <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ── Passo 2: documento ── */}
      {passo === 2 && (
        <div>
          <p className="text-sm text-slate-400 mb-4">
            Fotografe o Bilhete de Identidade. Pode saltar e recolher depois — mas
            sem documento o profissional não passa da verificação.
          </p>

          <Campo rotulo="Número do BI" id="bi" ajuda="Formato 000000000LA000">
            <input id="bi" className={entrada} value={bi}
              onChange={(e) => setBi(e.target.value.toUpperCase())} autoComplete="off" />
          </Campo>

          <Captura nome="profile_photo" rotulo="Fotografia do profissional"
            ficheiro={fotos.profile_photo} obrigatorio={!!bi.trim()}
            onEscolher={(f) => setFotos((v) => ({ ...v, profile_photo: f }))} />
          <Captura nome="bi_photo" rotulo="BI — frente"
            ficheiro={fotos.bi_photo} obrigatorio={!!bi.trim()}
            onEscolher={(f) => setFotos((v) => ({ ...v, bi_photo: f }))} />
          <Captura nome="bi_verso" rotulo="BI — verso"
            ficheiro={fotos.bi_verso}
            onEscolher={(f) => setFotos((v) => ({ ...v, bi_verso: f }))} />

          <button
            onClick={() => setPasso(3)}
            disabled={!!bi.trim() && (!fotos.profile_photo || !fotos.bi_photo)}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-blue-600
                       disabled:opacity-40 font-semibold mt-3"
          >
            Continuar <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ── Passo 3: confirmar ── */}
      {passo === 3 && (
        <div>
          <p className="text-sm text-slate-400 mb-4">
            Confirme com o profissional antes de enviar.
          </p>

          <dl className="rounded-xl bg-slate-900 border border-slate-800 divide-y divide-slate-800">
            {[
              ['Nome', dados.full_name],
              ['Telefone', dados.phone_number],
              ['Nascimento', dados.date_of_birth || '—'],
              ['Zona', zonas.find((z) => String(z.zone_id) === String(dados.zone_id))?.nome || '—'],
              ['Profissão', dados.profession || '—'],
              ['Especialidades', dados.specialties || '—'],
              ['Experiência', dados.years_experience ? `${dados.years_experience} anos` : '—'],
              ['BI', bi || '— (recolher depois)'],
              ['Fotografias', `${Object.values(fotos).filter(Boolean).length} de 3`],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-3 px-3 py-2.5 text-sm">
                <dt className="text-slate-500 w-32 shrink-0">{k}</dt>
                <dd className="flex-1 min-w-0 break-words">{v}</dd>
              </div>
            ))}
          </dl>

          <p className="text-xs text-slate-500 mt-4">
            Ao enviar, declara que confirmou os dados com o próprio e que ele aceitou
            os Termos da Dozy.
          </p>

          <div className="flex gap-2 mt-4">
            <button onClick={() => setPasso(2)} disabled={aEnviar}
              className="grid place-items-center w-14 rounded-xl bg-slate-800">
              <X className="w-5 h-5" />
            </button>
            <button
              onClick={submeter}
              disabled={aEnviar}
              className="flex-1 flex items-center justify-center gap-2 py-4 rounded-xl bg-green-600
                         disabled:opacity-50 font-bold"
            >
              {aEnviar ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              {aEnviar ? 'A enviar…' : 'Enviar para verificação'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldNewWorker;
